// Evidence recommendations based on card brand and reason code
export interface EvidenceItem {
  title: string;
  description: string | null;
}
export interface EvidenceRecommendation {
  title: string;
  description: string;
  learnMoreLink: string | null;
  recommendations: EvidenceItem[];
}

// Type for the evidence map structure
type EvidenceMap = {
  [cardBrand: string]: {
    [reasonCode: string]: EvidenceRecommendation;
  };
};

// Evidence recommendations database - organized by card brand and reason code
const evidenceRecommendations: EvidenceMap = {
  visa: {
    '13.2': {
      title: 'Subscription cancellation evidence',
      description: 'To support your challenge that the subscription was not properly canceled or that the charge was valid, you should provide evidence showing the subscription terms and cancellation policy.',
      learnMoreLink: 'Learn more about subscription cancellation disputes.',
      recommendations: [{
        title: 'Cancellation policy',
        description: 'Show proof that the customer agreed to the subscription terms, including any cancellation deadlines.'
      }, {
        title: 'Cancellation request records',
        description: 'If the customer never submitted a cancellation request or canceled after the charge was processed, provide system logs showing the exact date and time.'
      }, {
        title: 'Usage logs (for digital services)',
        description: 'If the customer accessed the service post-cancellation, include timestamps and IP logs.'
      }, {
        title: 'Shipping and fulfillment records (for physical goods)',
        description: 'If a recurring order was already shipped before cancellation, provide tracking information.'
      }, {
        title: 'Customer communications',
        description: 'The customer is obligated to proactively communicate the issue before filing a dispute. If the customer was informed of their billing cycle or non-refundable terms, include chat logs or email confirmations.'
      }, {
        title: 'Refund records (if applicable)',
        description: 'If a refund was issued before the dispute, show proof of the transaction.'
      }]
    },
    '10.4': {
      title: 'Fraudulent transaction evidence',
      description: "To support your challenge that the transaction was legitimate and authorized by the cardholder, provide evidence showing the customer's purchasing history and authentication.",
      learnMoreLink: 'Learn more about fraud disputes.',
      recommendations: [{
        title: 'Previous transaction records with the customer',
        description: 'Show proof of prior successful transactions with the same customer using the same payment method.'
      }, {
        title: 'Customer authentication records',
        description: 'Provide evidence of 3DS authentication, CVV verification, or other security checks that were passed.'
      }, {
        title: 'Communication history (emails, messages)',
        description: 'Include any correspondence with the customer that confirms they authorized the transaction.'
      }, {
        title: 'Proof of delivery or service completion',
        description: "Show that the product was delivered or service was rendered to the customer's verified address."
      }, {
        title: 'IP address and device fingerprinting',
        description: "Provide logs showing the transaction came from the customer's known IP address or device."
      }]
    }
  },
  mastercard: {
    '4834': {
      title: 'Duplicate transaction evidence',
      description: 'To support your challenge that the charge was not a duplicate or that multiple charges were legitimate, provide evidence showing the transaction details and processing records.',
      learnMoreLink: 'Learn more about duplicate transaction disputes.',
      recommendations: [{
        title: 'Transaction logs',
        description: 'Show that only one charge was successfully processed.'
      }, {
        title: 'Payment processing records',
        description: 'If a duplicate did occur but was voided, reversed, or refunded, provide documentation.'
      }, {
        title: 'Receipts and invoices',
        description: 'Show that the charges correspond to different transactions, dates, or services.'
      }, {
        title: 'Recurring or installment payment agreements',
        description: 'If the charge is part of a subscription or payment plan, provide the agreement.'
      }, {
        title: 'Pre-authorization and settlement proof',
        description: 'Demonstrate that one charge was an authorization hold that later cleared, not an actual duplicate transaction.'
      }, {
        title: 'Customer communications',
        description: 'If the cardholder reached out before filing a chargeback, include any relevant interactions confirming their misunderstanding.'
      }]
    },
    '4837': {
      title: 'Fraudulent transaction evidence',
      description: 'To support your challenge that the transaction was legitimate, provide evidence showing the customer authorized the purchase.',
      learnMoreLink: 'Learn more about Mastercard fraud disputes.',
      recommendations: [{
        title: 'Previous transaction records',
        description: 'Show proof of prior successful transactions with the same customer.'
      }, {
        title: 'Customer authentication records',
        description: 'Provide evidence of 3DS authentication or other security checks.'
      }, {
        title: 'Delivery confirmation',
        description: "Show that the product was delivered to the customer's verified address."
      }, {
        title: 'Communication history',
        description: 'Include correspondence confirming the customer authorized the transaction.'
      }]
    }
  },
  amex: {
    C02: {
      title: 'Credit not processed evidence',
      description: 'To support your challenge that the credit was properly processed, provide evidence of the refund or credit issued to the customer.',
      learnMoreLink: 'Learn more about American Express credit disputes.',
      recommendations: [{
        title: 'Refund transaction records',
        description: "Provide proof that a refund was issued to the customer's card."
      }, {
        title: 'Credit memo or receipt',
        description: 'Show documentation of the credit issued to the customer.'
      }, {
        title: 'Customer communication about refund',
        description: 'Include emails or messages confirming the refund was processed.'
      }, {
        title: 'Bank statement showing credit',
        description: "If available, provide evidence that the credit appeared on the customer's statement."
      }]
    },
    F29: {
      title: 'Fraudulent transaction evidence',
      description: 'To support your challenge that the transaction was legitimate and authorized by the cardholder, provide evidence showing customer authentication and transaction legitimacy.',
      learnMoreLink: 'Learn more about American Express fraud disputes.',
      recommendations: [{
        title: '3D Secure authentication records',
        description: 'Show that the cardholder completed multi-factor authentication.'
      }, {
        title: 'AVS and CID verification results',
        description: 'Provide evidence that the billing address and security code matched.'
      }, {
        title: 'IP address and device fingerprinting',
        description: 'Show that the transaction was made from a familiar device or location.'
      }, {
        title: 'Order confirmation and invoices',
        description: 'Include records of purchase confirmation sent to the cardholder.'
      }, {
        title: 'Delivery tracking and signed receipts',
        description: 'If physical goods were delivered, provide tracking logs and signed delivery confirmation.'
      }, {
        title: 'Customer communications',
        description: 'If the cardholder contacted you before disputing (for example, support inquiries, refund requests), include those interactions.'
      }]
    }
  },
  discover: {}
};

// Default/fallback evidence for when no specific mapping exists
const defaultEvidence: EvidenceRecommendation = {
  title: 'Supporting evidence',
  description: 'Upload documents that support your challenge. Providing strong evidence increases your chances of winning the dispute.',
  learnMoreLink: null,
  recommendations: [{
    title: 'Previous transaction records with the customer',
    description: null
  }, {
    title: 'Communication history (emails, messages)',
    description: null
  }, {
    title: 'Proof of delivery or service completion',
    description: null
  }]
};

/**
 * Get recommended evidence based on card brand and reason code
 * @param cardBrand - The card brand (Visa, Mastercard, Amex, Discover)
 * @param reasonCode - The network reason code (e.g., "13.2", "10.4")
 * @returns Evidence recommendation (returns default if specific mapping not found)
 */
export function getEvidenceRecommendation(cardBrand: string, reasonCode: string): EvidenceRecommendation {
  // LOGGING: Track the lookup process
  console.group('📋 getEvidenceRecommendation');
  console.log('Input cardBrand:', cardBrand);
  console.log('Input reasonCode:', reasonCode);
  const normalizedBrand = cardBrand.toLowerCase();
  console.log('Normalized brand:', normalizedBrand);

  // Check if brand exists in our map
  const brandExists = !!evidenceRecommendations[normalizedBrand];
  console.log('Brand exists in map:', brandExists);
  if (brandExists) {
    console.log('Available reason codes for this brand:', Object.keys(evidenceRecommendations[normalizedBrand]));
  }
  const recommendation = evidenceRecommendations[normalizedBrand]?.[reasonCode];
  if (recommendation) {
    console.log('✅ Found specific recommendation:', {
      title: recommendation.title,
      recommendationsCount: recommendation.recommendations.length
    });
  } else {
    console.log('⚠️ No specific recommendation found, using default');
    console.log('Reason: Brand exists?', brandExists, '| Reason code exists?', !!evidenceRecommendations[normalizedBrand]?.[reasonCode]);
  }
  console.groupEnd();
  return recommendation || defaultEvidence;
}

/**
 * Check if specific evidence recommendations exist for a given card brand and reason code
 * @param cardBrand - The card brand
 * @param reasonCode - The network reason code
 * @returns true if specific recommendations exist, false if would use default
 */
export function hasSpecificEvidenceRecommendation(cardBrand: string, reasonCode: string): boolean {
  const normalizedBrand = cardBrand.toLowerCase();
  return !!evidenceRecommendations[normalizedBrand]?.[reasonCode];
}

/**
 * Get all supported card brands
 * @returns Array of supported card brand names
 */
export function getSupportedCardBrands(): string[] {
  return Object.keys(evidenceRecommendations);
}

/**
 * Get all supported reason codes for a specific card brand
 * @param cardBrand - The card brand
 * @returns Array of supported reason codes for that brand
 */
export function getSupportedReasonCodes(cardBrand: string): string[] {
  const normalizedBrand = cardBrand.toLowerCase();
  return Object.keys(evidenceRecommendations[normalizedBrand] || {});
}